import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import suggestionConfig from '../../lib/tiptap/suggestionConfig';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string, hasTags: boolean) => void;
  placeholder?: string;
  options: string[];
  minHeight?: string;
  mentionTrigger?: string;
  /**
   * When true, typing free text is allowed, but as soon as a tag (mention)
   * is inserted, every plain-text node is cleared — leaving only tags.
   */
  tagsOnly?: boolean;

  /**
   * Maximum number of tags allowed. `undefined` = unlimited.
   * `0` = no tags allowed.
   */
  maxTags?: number;
}

export type RichTextEditorHandle = {
  clearInput: () => void;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export type AnyNode = {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: AnyNode[];
  [k: string]: unknown;
};

export function collectMentions(json: AnyNode): AnyNode[] {
  const out: AnyNode[] = [];
  const walk = (node: AnyNode | undefined) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'mention') out.push(node);
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(json);
  return out;
}

/**
 * Convert TipTap HTML into a plain template string.
 *   <span data-type="mention" data-id="qr" ...>{{qr}}</span>  →  {{qr}}
 *   Paragraph boundaries become newlines.
 *   All other tags are stripped; entities are decoded.
 */
function stripParagraphs(html: string): string {
  return (
    html
      // 1. Mentions → {{id}}
      .replace(/<span[^>]*data-type="mention"[^>]*data-id="([^"]+)"[^>]*>.*?<\/span>/gi, '{{$1}}')
      // 2. Paragraph boundaries → newline
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      // 3. Remaining tags → removed
      .replace(/<[^>]+>/g, '')
      // 4. Decode entities TipTap emits
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim()
  );
}

/**
 * Convert a template string back into TipTap-compatible HTML.
 *   {{qr}}  →  <span data-type="mention" data-id="qr" data-label="qr">@qr</span>
 */
function fromTemplateString(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return text
    .split('\n')
    .map((line) => {
      // Split on {{id}} tokens, keep the delimiters.
      const parts = line.split(/(\{\{[^}]+\}\})/g);
      const inline = parts
        .map((part) => {
          const m = /^\{\{([^}]+)\}\}$/.exec(part);
          if (m) {
            const id = m[1];
            return `<span data-type="mention" data-id="${id}" data-label="${id}" data-mention-suggestion-char="@">@${id}</span>`;
          }
          return escape(part);
        })
        .join('');
      return `<p>${inline || '<br>'}</p>`;
    })
    .join('');
}

/** Build a doc containing only the given mention nodes, space-separated. */
function buildTagOnlyContent(mentions: AnyNode[]) {
  const inline = mentions.flatMap((m, i) => (i === 0 ? [m] : [{ type: 'text', text: ' ' }, m]));
  return {
    type: 'doc',
    content: [inline.length > 0 ? { type: 'paragraph', content: inline } : { type: 'paragraph' }],
  };
}

const VariableInfusedTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      placeholder = 'Write something...',
      options = [],
      minHeight,
      tagsOnly = false,
      maxTags,
      mentionTrigger = '@',
    } = props;

    const hasTagsRef = useRef(false);
    // Tag count from the previous update, used to detect new tag insertions.
    const prevTagCountRef = useRef(0);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          bulletList: false,
          orderedList: false,
          heading: false,
          link: false,
        }),
        Placeholder.configure({
          placeholder,
        }),
        Mention.configure({
          HTMLAttributes: {
            class: 'bg-accent/10 text-accent px-1 py-0.5 rounded cursor-pointer hover:bg-accent/20',
          },
          suggestion: suggestionConfig(options),
        }),
      ],
      content: fromTemplateString(value),
      onUpdate: ({ editor }) => {
        const json = editor.getJSON() as AnyNode;
        let mentions = collectMentions(json);
        const rawCount = mentions.length;

        if (maxTags != null && mentions.length > maxTags) {
          mentions = mentions.slice(0, maxTags);
        }

        const tagAdded = mentions.length > prevTagCountRef.current;
        const overLimit = maxTags != null && rawCount > maxTags;

        if (overLimit || (tagsOnly && tagAdded && !overLimit)) {
          editor.commands.setContent(buildTagOnlyContent(mentions), { emitUpdate: false });
        }

        const finalMentions = collectMentions(editor.getJSON() as AnyNode);
        prevTagCountRef.current = finalMentions.length;
        hasTagsRef.current = finalMentions.length > 0;
        onChange(stripParagraphs(editor.getHTML()), hasTagsRef.current);
      },
      editorProps: {
        attributes: {
          class: 'field-input',
          style: `min-height: ${minHeight}`,
        },

        handleTextInput: (_view, _from, _to, text) => {
          if (!tagsOnly) return false;
          if (text === mentionTrigger) return false;
          return hasTagsRef.current; // returning true = "handled" = swallowed
        },

        handlePaste: () => {
          if (!tagsOnly) return false;
          return hasTagsRef.current;
        },

        handleDrop: () => {
          if (!tagsOnly) return false;
          return hasTagsRef.current;
        },

        handleKeyDown: (_view, event) => {
          if (!tagsOnly || !hasTagsRef.current) return false;
          // No new paragraphs / lines in chips-only mode.
          if (event.key === 'Enter') return true;
          return false;
        },
      },
    });

    // Sync the tag counter with the initial content once the editor mounts.
    useEffect(() => {
      if (editor) {
        const mentions = collectMentions(editor.getJSON() as AnyNode);
        prevTagCountRef.current = mentions.length;
        hasTagsRef.current = mentions.length > 0;
      }
    }, [editor]);

    useImperativeHandle(ref, () => ({
      clearInput() {
        editor.commands.clearContent(true);
        prevTagCountRef.current = 0;
        hasTagsRef.current = false;
      },
    }));

    if (!editor) {
      return (
        <div className="border-foreground/20 bg-background rounded-lg border" style={{ minHeight }}>
          <div className="prose prose-sm max-w-none p-3">Loading editor...</div>
        </div>
      );
    }

    return <EditorContent editor={editor} />;
  },
);

export default VariableInfusedTextEditor;
